import React, { useState, useEffect, useRef } from 'react';
import { 
    FaTimes, 
    FaSearch, 
    FaFileAlt, 
    FaDownload, 
    FaEye, 
    FaInfoCircle,
    FaFilePdf,
    FaFileWord,
    FaFileExcel,
    FaFilePowerpoint,
    FaFileImage,
    FaFileArchive,
    FaFileCode,
    FaFileAudio,
    FaFileVideo,
    FaFile,
    FaFolder,
    FaPlus,
    FaEdit,
    FaTrash
} from 'react-icons/fa';
import { useMsal } from '@azure/msal-react';
import styles from './ClientDocumentsModal.module.css';
import { vwClientDocumentsService, VWClientDocumentsHelper } from '../../services/vwClientDocumentsService';
import { documentService } from '../../services/documentService';
import OfficePreviewService from '../../services/officePreviewService';
import DocumentUpload from './DocumentUpload';
import { useAccessToken, useLockBodyScroll } from '../../hooks';
import { useAlert } from '../../context/AlertContext';
import { filePreviewRequest } from '../../config/authConfig';

/**
 * Helper function to get the appropriate icon component based on file type
 * @param {string} iconType - The icon type from VWClientDocumentsHelper
 * @returns {JSX.Element} The appropriate icon component
 */
const getFileIcon = (iconType) => {
    const iconProps = { size: 28 };
    
    const iconMap = {
        'pdf': <FaFilePdf {...iconProps} style={{ color: '#E74C3C' }} />,
        'word': <FaFileWord {...iconProps} style={{ color: '#2C3E96' }} />,
        'excel': <FaFileExcel {...iconProps} style={{ color: '#1D6F42' }} />,
        'powerpoint': <FaFilePowerpoint {...iconProps} style={{ color: '#D24726' }} />,
        'image': <FaFileImage {...iconProps} style={{ color: '#3498DB' }} />,
        'archive': <FaFileArchive {...iconProps} style={{ color: '#95A5A6' }} />,
        'text': <FaFileAlt {...iconProps} style={{ color: '#7F8C8D' }} />,
        'code': <FaFileCode {...iconProps} style={{ color: '#9B59B6' }} />,
        'audio': <FaFileAudio {...iconProps} style={{ color: '#E67E22' }} />,
        'video': <FaFileVideo {...iconProps} style={{ color: '#16A085' }} />,
        'file': <FaFile {...iconProps} style={{ color: '#95A5A6' }} />
    };
    
    return iconMap[iconType] || iconMap['file'];
};

/**
 * Document Card Component
 * Displays individual document information in a card format
 */
const DocumentCard = ({ document, onView, onDownload, onInfo, onRename, onDelete, isDownloading, isViewing, getDisplayFileName, getDisplayContainer }) => {
    const extension = VWClientDocumentsHelper.getFileExtension(document);
    const iconType = VWClientDocumentsHelper.getFileIconType(document);
    const icon = getFileIcon(iconType);
    const isModified = VWClientDocumentsHelper.isModified(document);
    const relativeTime = VWClientDocumentsHelper.getRelativeTime(document.createdAt);
    const createdDate = new Date(document.createdAt).toLocaleDateString();
    const isViewable = documentService.isViewableInBrowser(document.fileName);
    
    // Get user-friendly display name (without clientId prefix)
    const displayFileName = getDisplayFileName ? getDisplayFileName(document.fileName) : document.fileName;
    // Get user-friendly container name (clientCode instead of 'client')
    const displayContainer = getDisplayContainer ? getDisplayContainer() : document.container;

    return (
        <div className={styles.documentCard}>
            {/* Icon */}
            <div className={styles.documentIcon}>{icon}</div>
            
            {/* File Name and Extension */}
            <div className={styles.documentName}>
                <span className={styles.fileName}>{displayFileName}</span>
                {extension && <span className={styles.documentExtension}>{extension.toUpperCase()}</span>}
            </div>
            
            {/* Container */}
            <div className={styles.documentContainer}>
                <FaFolder size={12} style={{ marginRight: '4px' }} />
                <span>{displayContainer}</span>
            </div>
            
            {/* Date */}
            <div className={styles.documentDate}>
                <span>{createdDate}</span>
                {isModified && <span className={styles.modifiedBadge}>Modified</span>}
            </div>
            
            {/* Actions */}
            <div className={styles.documentActions}>
                {isViewable && (
                    <button 
                        className={`${styles.btnView} ${isViewing ? styles.loading : ''}`}
                        title="View document"
                        onClick={() => onView(document)}
                        disabled={isViewing || isDownloading}
                    >
                        {!isViewing && <FaEye size={14} />}
                    </button>
                )}
                <button 
                    className={`${styles.btnDownload} ${isDownloading ? styles.loading : ''}`}
                    title="Download document"
                    onClick={() => onDownload(document)}
                    disabled={isViewing || isDownloading}
                >
                    {!isDownloading && <FaDownload size={14} />}
                </button>
                <button 
                    className={styles.btnRename} 
                    title="Rename document"
                    onClick={() => onRename(document)}
                    disabled={isViewing || isDownloading}
                >
                    <FaEdit size={14} />
                </button>
                <button 
                    className={styles.btnInfo} 
                    title="Document details"
                    onClick={() => onInfo(document)}
                    disabled={isViewing || isDownloading}
                >
                    <FaInfoCircle size={14} />
                </button>
                <button 
                    className={styles.btnDelete} 
                    title="Delete document"
                    onClick={() => onDelete(document)}
                    disabled={isViewing || isDownloading}
                >
                    <FaTrash size={14} />
                </button>
            </div>
        </div>
    );
};

/**
 * Container Group Component
 * Displays documents grouped by container
 */
const ContainerGroup = ({ container, documents, onView, onDownload, onInfo, onRename, onDelete, downloadingDocId, viewingDocId, getDisplayFileName, getDisplayContainer }) => {
    const [isExpanded, setIsExpanded] = useState(true);

    return (
        <div className={styles.containerGroup}>
            <div 
                className={styles.containerHeader}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <h3 className={styles.containerTitle}>
                    📁 {container} <span className={styles.count}>({documents.length})</span>
                </h3>
                <span className={styles.expandIcon}>{isExpanded ? '▼' : '▶'}</span>
            </div>
            
            {isExpanded && (
                <div className={styles.containerDocuments}>
                    {documents.map(doc => (
                        <DocumentCard 
                            key={doc.documentId} 
                            document={doc}
                            onView={onView}
                            onDownload={onDownload}
                            onInfo={onInfo}
                            onRename={onRename}
                            onDelete={onDelete}
                            isDownloading={downloadingDocId === doc.documentId}
                            isViewing={viewingDocId === doc.documentId}
                            getDisplayFileName={getDisplayFileName}
                            getDisplayContainer={getDisplayContainer}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

/**
 * Document Info Modal Component
 * Displays detailed information about a document
 */
const DocumentInfoModal = ({ document, isOpen, onClose, getDisplayFileName, getDisplayContainer }) => {
    if (!isOpen || !document) return null;
    
    // Get user-friendly display name (without clientId prefix)
    const displayFileName = getDisplayFileName ? getDisplayFileName(document.fileName) : document.fileName;
    // Get user-friendly container name (clientCode instead of 'client')
    const displayContainer = getDisplayContainer ? getDisplayContainer() : document.container;

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.infoModalContent}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>Document Details</h2>
                    <button className={styles.closeButton} onClick={onClose} title="Close">
                        <FaTimes />
                    </button>
                </div>
                <div className={styles.infoContent}>
                    <div className={styles.infoRow}>
                        <strong>File Name:</strong>
                        <span>{displayFileName}</span>
                    </div>
                    <div className={styles.infoRow}>
                        <strong>Container:</strong>
                        <span>{displayContainer}</span>
                    </div>
                    <div className={styles.infoRow}>
                        <strong>Document ID:</strong>
                        <span>{document.documentId}</span>
                    </div>
                    <div className={styles.infoRow}>
                        <strong>Blob Path:</strong>
                        <span className={styles.blobPath}>{document.blobPath || 'N/A'}</span>
                    </div>
                    <div className={styles.infoRow}>
                        <strong>Created:</strong>
                        <span>{VWClientDocumentsHelper.formatDate(document.createdAt)}</span>
                    </div>
                    <div className={styles.infoRow}>
                        <strong>Last Modified:</strong>
                        <span>{VWClientDocumentsHelper.formatDate(document.lastModifiedAt)}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * Delete Document Confirmation Dialog Component
 * Modal for confirming document deletion
 */
const DeleteDocumentDialog = ({ document, isOpen, onClose, onDeleteSuccess, getDisplayFileName, clientId }) => {
    const { getBackendAccessToken } = useAccessToken();
    const { setErrorAlert, setInfoAlert } = useAlert();
    
    const displayFileName = getDisplayFileName ? getDisplayFileName(document?.fileName) : document?.fileName;
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState(null);

    if (!isOpen || !document) return null;

    const handleDelete = async () => {
        setError(null);
        setDeleting(true);

        try {
            const accessToken = await getBackendAccessToken();
            
            console.log('Delete operation:', {
                documentId: document.documentId,
                fileName: document.fileName,
                displayFileName
            });
            
            // Single API call to delete document
            // This endpoint will:
            // ✅ Delete the physical file from Azure Blob Storage
            // ✅ Delete the document record from the Documents table
            // ✅ Automatically delete all related entries in DocumentClients table (CASCADE DELETE)
            await documentService.deleteById(accessToken, document.documentId);
            
            console.log('✅ Document deleted successfully (including blob storage, database record, and all client assignments)');
            
            // Show success message
            setInfoAlert(`Document "${displayFileName}" deleted successfully`);
            
            // Call success callback to update UI
            if (onDeleteSuccess) {
                onDeleteSuccess(document.documentId);
            }

            onClose();
        } catch (err) {
            console.error('❌ Delete error:', err);
            const errorMessage = err.message || 'Delete failed';
            
            // Handle specific errors
            if (errorMessage.toLowerCase().includes('not found') || errorMessage.toLowerCase().includes('404')) {
                setError('Document not found. It may have already been deleted.');
                // Still try to update the UI since the document is gone
                if (onDeleteSuccess) {
                    onDeleteSuccess(document.documentId);
                }
                // Auto-close after showing the error
                setTimeout(() => {
                    onClose();
                }, 2000);
            } else if (errorMessage.toLowerCase().includes('blob')) {
                setError('Storage error: Unable to delete the file. Please try again or contact support.');
            } else {
                setError(errorMessage);
            }
            
            setErrorAlert(`Failed to delete document: ${errorMessage}`);
        } finally {
            setDeleting(false);
        }
    };

    const handleClose = () => {
        if (!deleting) {
            setError(null);
            onClose();
        }
    };

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.deleteDialogContent}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>Delete Document</h2>
                    <button 
                        className={styles.closeButton} 
                        onClick={handleClose} 
                        title="Close"
                        disabled={deleting}
                    >
                        <FaTimes />
                    </button>
                </div>
                
                <div className={styles.deleteContent}>
                    <div className={styles.warningIcon}>
                        <FaTrash size={48} color="#e74c3c" />
                    </div>

                    <p className={styles.deleteWarning}>
                        Are you sure you want to delete this document? This action cannot be undone.
                    </p>

                    <div className={styles.documentInfo}>
                        <p><strong>File:</strong> {displayFileName}</p>
                        <p><strong>Document ID:</strong> {document.documentId}</p>
                        <p><strong>Client ID:</strong> {document.clientId || clientId}</p>
                    </div>

                    {error && <div className={styles.errorMessage}>{error}</div>}

                    <div className={styles.dialogActions}>
                        <button
                            type="button"
                            onClick={handleClose}
                            className={styles.btnSecondary}
                            disabled={deleting}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleDelete}
                            className={styles.btnDanger}
                            disabled={deleting}
                        >
                            {deleting ? 'Deleting...' : 'Delete Document'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * Rename Document Dialog Component
 * Modal for renaming a document
 */
const RenameDocumentDialog = ({ document, isOpen, onClose, onRenameSuccess, getDisplayFileName, clientId, clientCode }) => {
    const { getBackendAccessToken } = useAccessToken();
    const { setErrorAlert, setInfoAlert } = useAlert();
    
    const displayFileName = getDisplayFileName ? getDisplayFileName(document?.fileName) : document?.fileName;
    const [newFileName, setNewFileName] = useState(displayFileName || '');
    const [renaming, setRenaming] = useState(false);
    const [error, setError] = useState(null);

    // Update newFileName when document changes
    useEffect(() => {
        if (document) {
            const display = getDisplayFileName ? getDisplayFileName(document.fileName) : document.fileName;
            setNewFileName(display);
            setError(null);
        }
    }, [document, getDisplayFileName]);

    if (!isOpen || !document) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        const trimmedFileName = newFileName.trim();

        if (!trimmedFileName) {
            setError('Please enter a filename');
            return;
        }

        if (trimmedFileName === displayFileName) {
            setError('New filename is the same as the current filename');
            return;
        }

        // Validate file extension - do not allow changing document type
        const currentExt = displayFileName.includes('.') ? displayFileName.substring(displayFileName.lastIndexOf('.')).toLowerCase() : '';
        const newExt = trimmedFileName.includes('.') ? trimmedFileName.substring(trimmedFileName.lastIndexOf('.')).toLowerCase() : '';
        
        if (currentExt && newExt && currentExt !== newExt) {
            setError(`Document type cannot be changed during rename. If you need to change the document type from ${currentExt} to ${newExt}, it must be resubmitted as a new document.`);
            return;
        }

        setRenaming(true);

        try {
            const accessToken = await getBackendAccessToken();
            
            // Construct the full filename with clientId prefix (if applicable)
            // The backend expects the full path with clientId/filename format
            const fullNewFileName = clientId ? `${clientId}/${trimmedFileName}` : trimmedFileName;
            
            const response = await documentService.rename(
                accessToken,
                document.documentId,
                fullNewFileName
            );

            setInfoAlert(`Document renamed successfully to "${trimmedFileName}"`);
            
            // Call success callback with updated document
            if (onRenameSuccess && response.document) {
                onRenameSuccess(response.document);
            }

            onClose();
        } catch (err) {
            console.error('Rename error:', err);
            const errorMessage = err.message || 'Rename failed';
            
            // Handle specific errors
            if (errorMessage.toLowerCase().includes('already exists')) {
                setError('A document with that name already exists');
            } else if (errorMessage.toLowerCase().includes('not found')) {
                setError('Document not found. It may have been deleted.');
            } else if (errorMessage.toLowerCase().includes('failed to copy blob')) {
                setError('Failed to rename document in storage. The file may be locked or in use. Please try again.');
            } else if (errorMessage.toLowerCase().includes('blob')) {
                setError('Storage error: Unable to rename the file. Please try again or contact support.');
            } else {
                setError(errorMessage);
            }
            
            // Also show error alert for critical failures
            setErrorAlert(`Failed to rename document: ${errorMessage}`);
        } finally {
            setRenaming(false);
        }
    };

    const handleClose = () => {
        if (!renaming) {
            setError(null);
            onClose();
        }
    };

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.renameDialogContent}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>Rename Document</h2>
                    <button 
                        className={styles.closeButton} 
                        onClick={handleClose} 
                        title="Close"
                        disabled={renaming}
                    >
                        <FaTimes />
                    </button>
                </div>
                
                <div className={styles.renameContent}>
                    <div className={styles.currentInfo}>
                        <p><strong>Current filename:</strong> {displayFileName}</p>
                        <p><strong>Client:</strong> {clientCode || 'N/A'}</p>
                    </div>

                    {error && <div className={styles.errorMessage}>{error}</div>}

                    <form onSubmit={handleSubmit}>
                        <div className={styles.formGroup}>
                            <label htmlFor="newFileName">New Filename</label>
                            <input
                                id="newFileName"
                                type="text"
                                value={newFileName}
                                onChange={(e) => setNewFileName(e.target.value)}
                                placeholder="Enter new filename..."
                                required
                                autoFocus
                                disabled={renaming}
                                className={styles.renameInput}
                            />
                        </div>

                        <div className={styles.dialogActions}>
                            <button
                                type="button"
                                onClick={handleClose}
                                className={styles.btnSecondary}
                                disabled={renaming}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className={styles.btnPrimary}
                                disabled={renaming}
                            >
                                {renaming ? 'Renaming...' : 'Rename'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

/**
 * Client Documents Modal Component
 * Modal popup for displaying client documents with search and filter functionality
 */
const ClientDocumentsModal = ({ clientId, clientName, clientCode, isOpen, onClose, onDataChanged }) => {
    const { getBackendAccessToken } = useAccessToken();
    const { instance } = useMsal();
    const { setErrorAlert, setInfoAlert, setIsModalOpen } = useAlert();

    // Lock body scroll when modal is open
    useLockBodyScroll(isOpen);

    // State management
    const [documents, setDocuments] = useState([]);
    const [filteredDocuments, setFilteredDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('created'); // 'created', 'modified', 'name'
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [showRenameDialog, setShowRenameDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [renamingDocument, setRenamingDocument] = useState(null);
    const [deletingDocument, setDeletingDocument] = useState(null);
    const [downloadingDocId, setDownloadingDocId] = useState(null);
    const [viewingDocId, setViewingDocId] = useState(null);
    const [showUpload, setShowUpload] = useState(false);

    // Track previous open state to only load when transitioning from closed to open
    const prevIsOpenRef = useRef(false);

    /**
     * Convert fileName to display format (remove clientId prefix)
     * If fileName is in format "123/document.pdf", return just "document.pdf"
     * @param {string} fileName - Original filename from backend
     * @returns {string} Filename without the clientId prefix
     */
    const getDisplayFileName = (fileName) => {
        if (!fileName || !clientId) return fileName;
        
        // Check if filename starts with "clientId/"
        const prefix = `${clientId}/`;
        if (fileName.startsWith(prefix)) {
            // Remove the clientId prefix, return just the filename
            return fileName.substring(prefix.length);
        }
        
        return fileName;
    };

    /**
     * Get display container name (show clientCode instead of 'client')
     * @returns {string} Client code for display
     */
    const getDisplayContainer = () => {
        return clientCode || 'client';
    };

    /**
     * Load documents for the client
     * @param {boolean} bypassCache - Whether to bypass cache (use after upload)
     */
    const loadDocuments = async (bypassCache = false) => {
        if (!clientId) return;

        try {
            setLoading(true);
            const accessToken = await getBackendAccessToken();
            
            // Fetch documents ordered by creation date
            const docs = await vwClientDocumentsService.getDocumentsByClientIdOrderedByDate(accessToken, clientId, bypassCache);
            
            setDocuments(docs);
            // Don't set filteredDocuments here - let the useEffect handle it
        } catch (error) {
            console.error('Failed to load client documents:', error);
            setErrorAlert('Failed to load documents. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Filter and sort documents based on current criteria
     */
    useEffect(() => {
        let filtered = [...documents];

        // Apply search filter
        if (searchTerm.trim()) {
            const query = searchTerm.toLowerCase();
            filtered = filtered.filter(doc =>
                doc.fileName.toLowerCase().includes(query)
            );
        }

        // Apply sorting
        filtered = VWClientDocumentsHelper.sortDocuments(filtered, sortBy, 'desc');

        setFilteredDocuments(filtered);
    }, [searchTerm, sortBy, documents]);

    /**
     * Handle viewing a document in a new tab
     * Enhanced with Office 365 preview support for Word, Excel, PowerPoint files
     */
    const handleViewDocument = async (document) => {
        if (!document || !document.documentId) return;

        try {
            setViewingDocId(document.documentId);
            const accessToken = await getBackendAccessToken();
            
            // Download the blob
            const blob = await documentService.download(accessToken, document.documentId);
            
            // Check if it's an Office document and get MSAL token if needed
            let msalToken = null;
            if (OfficePreviewService.isOfficeDocument(document.fileName)) {
                try {
                    const accounts = instance.getAllAccounts();
                    if (accounts.length > 0) {
                        const request = {
                            ...filePreviewRequest,
                            account: accounts[0]
                        };

                        try {
                            const response = await instance.acquireTokenSilent(request);
                            msalToken = response.accessToken;
                        } catch (silentError) {
                            // If silent acquisition fails, try popup
                            const response = await instance.acquireTokenPopup(filePreviewRequest);
                            msalToken = response.accessToken;
                        }
                    }
                } catch (tokenError) {
                    console.warn('Failed to acquire MSAL token for Office preview, falling back to standard preview:', tokenError);
                }
            }
            
            // Open in new tab (will use Office preview if available)
            await documentService.openFile(blob, document.fileName, msalToken);
            
            // Show appropriate message
            if (msalToken && OfficePreviewService.isOfficeDocument(document.fileName)) {
                setInfoAlert(`Opening ${getDisplayFileName(document.fileName)} in Microsoft 365 Online...`);
            }
            
        } catch (error) {
            console.error('Failed to view document:', error);
            setErrorAlert(`Failed to view document: ${error.message || 'Unknown error'}`);
        } finally {
            setViewingDocId(null);
        }
    };

    /**
     * Handle downloading a document
     */
    const handleDownloadDocument = async (document) => {
        if (!document || !document.documentId) return;

        try {
            setDownloadingDocId(document.documentId);
            const accessToken = await getBackendAccessToken();
            
            // Download the blob
            const blob = await documentService.download(accessToken, document.documentId);
            
            // Trigger download
            documentService.downloadFile(blob, document.fileName);
            
            // Show user-friendly filename in alert
            const displayFileName = getDisplayFileName(document.fileName);
            setInfoAlert(`Document "${displayFileName}" downloaded successfully`);
        } catch (error) {
            console.error('Failed to download document:', error);
            setErrorAlert(`Failed to download document: ${error.message || 'Unknown error'}`);
        } finally {
            setDownloadingDocId(null);
        }
    };

    /**
     * Handle showing document info
     */
    const handleShowInfo = (document) => {
        setSelectedDocument(document);
        setShowInfoModal(true);
    };

    /**
     * Handle closing info modal
     */
    const handleCloseInfoModal = () => {
        setShowInfoModal(false);
        setSelectedDocument(null);
    };

    /**
     * Handle showing rename dialog
     */
    const handleShowRename = (document) => {
        setRenamingDocument(document);
        setShowRenameDialog(true);
    };

    /**
     * Handle closing rename dialog
     */
    const handleCloseRenameDialog = () => {
        setShowRenameDialog(false);
        setRenamingDocument(null);
    };

    /**
     * Handle successful rename
     */
    const handleRenameSuccess = async (updatedDocument) => {
        // Update the document in the list with the new filename
        setDocuments(prev => 
            prev.map(doc => 
                doc.documentId === updatedDocument.documentId 
                    ? { ...doc, ...updatedDocument }
                    : doc
            )
        );
        
        // Optionally reload documents to ensure consistency
        // Uncomment the line below if you want to refresh from server
        // await loadDocuments(true);
    };

    /**
     * Handle showing delete dialog
     */
    const handleShowDelete = (document) => {
        setDeletingDocument(document);
        setShowDeleteDialog(true);
    };

    /**
     * Handle closing delete dialog
     */
    const handleCloseDeleteDialog = () => {
        setShowDeleteDialog(false);
        setDeletingDocument(null);
    };

    /**
     * Handle successful delete
     */
    const handleDeleteSuccess = async (documentId) => {
        // Remove the document from the list
        setDocuments(prev => prev.filter(doc => doc.documentId !== documentId));
        
        // Optionally reload documents to ensure consistency
        // await loadDocuments(true);
    };

    /**
     * Handle successful upload
     */
    const handleUploadSuccess = async (results) => {
        // Add a delay to allow the database view to update
        // The VW_Client_Documents view may take a moment to reflect the new assignment
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Reload documents after successful upload with cache bypass
        await loadDocuments(true); // Pass true to bypass cache
        
        // Show success message
        const successCount = results?.filter(r => r && !r.assignmentError).length || 0;
        if (successCount > 0) {
            setInfoAlert(`Document list updated with ${successCount} new file(s)`);
        }
        
        // Notify parent that data has changed (refresh client list in background)
        if (onDataChanged) {
            onDataChanged();
        }
        
        // Close upload panel after refresh is complete
        setShowUpload(false);
    };

    // Load documents only when modal opens (not when it closes)
    // Only load when transitioning from closed to open, not on every render
    useEffect(() => {
        // Only load when modal transitions from closed to open
        const wasClosedNowOpen = !prevIsOpenRef.current && isOpen;
        
        if (wasClosedNowOpen && clientId) {
            loadDocuments();
        }
        
        // Update the ref for next render
        prevIsOpenRef.current = isOpen;
    }, [isOpen, clientId]);

    // Close modal on Escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    // Add/remove body attribute when modal opens/closes to block header interactions
    // Also notify AlertContext about modal state
    useEffect(() => {
        if (isOpen) {
            document.body.setAttribute('data-modal-open', 'true');
            setIsModalOpen(true);
        } else {
            document.body.removeAttribute('data-modal-open');
            setIsModalOpen(false);
        }

        // Cleanup on unmount
        return () => {
            document.body.removeAttribute('data-modal-open');
            setIsModalOpen(false);
        };
    }, [isOpen, setIsModalOpen]);

    // Don't render if not open
    if (!isOpen) return null;

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
                {/* Modal Header */}
                <div className={styles.modalHeader}>
                    <div className={styles.headerInfo}>
                        <FaFileAlt className={styles.headerIcon} />
                        <div>
                            <h2 className={styles.modalTitle}>Client Documents</h2>
                            <p className={styles.modalSubtitle}>
                                {clientCode ? `${clientCode} - ${clientName}` : clientName}
                            </p>
                        </div>
                    </div>
                    <button 
                        className={styles.closeButton} 
                        onClick={onClose}
                        title="Close"
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* Controls Section */}
                <div className={styles.controlsSection}>
                    {/* Upload Button */}
                    <div className={styles.uploadButtonContainer}>
                        <button 
                            className={styles.uploadButton}
                            onClick={() => setShowUpload(!showUpload)}
                            title="Upload documents"
                        >
                            <FaPlus /> Upload Documents
                        </button>
                    </div>

                    {/* Upload Component */}
                    {showUpload && (
                        <div className={styles.uploadSection}>
                            <DocumentUpload
                                clientId={clientId}
                                container="client"
                                existingDocuments={documents}
                                onUploadSuccess={handleUploadSuccess}
                                onClose={() => setShowUpload(false)}
                            />
                        </div>
                    )}

                    {/* Search Bar */}
                    <div className={styles.searchBar}>
                        <FaSearch className={styles.searchIcon} />
                        <input
                            type="text"
                            className={styles.searchInput}
                            placeholder="Search documents by file name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {/* Sort Controls */}
                    <div className={styles.filterControls}>
                        <div className={styles.filterGroup}>
                            <label>Sort by:</label>
                            <select 
                                value={sortBy} 
                                onChange={(e) => setSortBy(e.target.value)}
                                className={styles.filterSelect}
                            >
                                <option value="created">Created Date</option>
                                <option value="modified">Modified Date</option>
                                <option value="name">File Name</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Document Stats */}
                <div className={styles.documentStats}>
                    <span className={styles.statsItem}>
                        <strong>Total Documents:</strong> {filteredDocuments.length}
                    </span>
                    {searchTerm && (
                        <span className={styles.statsItem}>
                            <strong>Matching:</strong> "{searchTerm}"
                        </span>
                    )}
                </div>

                {/* Document List */}
                <div className={styles.documentList}>
                    {loading ? (
                        <div className={styles.loadingMessage}>
                            <div className={styles.spinner}></div>
                            <p>Loading documents...</p>
                        </div>
                    ) : filteredDocuments.length === 0 ? (
                        <div className={styles.noDocuments}>
                            <FaFileAlt className={styles.noDocumentsIcon} />
                            <p>
                                {searchTerm
                                    ? 'No documents match your search criteria.'
                                    : 'No documents found for this client.'}
                            </p>
                        </div>
                    ) : (
                        // List view - all documents
                        filteredDocuments.map(doc => (
                            <DocumentCard 
                                key={doc.documentId} 
                                document={doc}
                                onView={handleViewDocument}
                                onDownload={handleDownloadDocument}
                                onInfo={handleShowInfo}
                                onRename={handleShowRename}
                                onDelete={handleShowDelete}
                                isDownloading={downloadingDocId === doc.documentId}
                                isViewing={viewingDocId === doc.documentId}
                                getDisplayFileName={getDisplayFileName}
                                getDisplayContainer={getDisplayContainer}
                            />
                        ))
                    )}
                </div>

                {/* Document Info Modal */}
                <DocumentInfoModal 
                    document={selectedDocument}
                    isOpen={showInfoModal}
                    onClose={handleCloseInfoModal}
                    getDisplayFileName={getDisplayFileName}
                    getDisplayContainer={getDisplayContainer}
                />

                {/* Rename Document Dialog */}
                <RenameDocumentDialog
                    document={renamingDocument}
                    isOpen={showRenameDialog}
                    onClose={handleCloseRenameDialog}
                    onRenameSuccess={handleRenameSuccess}
                    getDisplayFileName={getDisplayFileName}
                    clientId={clientId}
                    clientCode={clientCode}
                />

                {/* Delete Document Dialog */}
                <DeleteDocumentDialog
                    document={deletingDocument}
                    isOpen={showDeleteDialog}
                    onClose={handleCloseDeleteDialog}
                    onDeleteSuccess={handleDeleteSuccess}
                    getDisplayFileName={getDisplayFileName}
                    clientId={clientId}
                />
            </div>
        </div>
    );
};

export default ClientDocumentsModal;
